import React from "react";
import { formatCurrency } from "../../utils/currencyUtils";

const fmtDateTime = (iso, timeZone) => {
  if (!iso) return null;
  const opts = { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true };
  if (timeZone) opts.timeZone = timeZone;
  return new Date(iso).toLocaleString("en-US", opts);
};

const tzAbbr = (timeZone) => {
  if (!timeZone) return null;
  try {
    // Extract the short timezone abbreviation, e.g. "PST", "PDT", "GST"
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(new Date());
    return parts.find(p => p.type === "timeZoneName")?.value || null;
  } catch {
    return null;
  }
};

// Map lineType snake_case → display label
const LINE_TYPE_LABEL = {
  base_pay: "Monthly Base Pay",
  lesson: "Lesson",
  demo_lesson: "Demo Lesson",
  demo_conversion_bonus: "Demo Conversion Bonus",
  counselor_demo_conversion_bonus: "Counselor Demo Conversion Bonus",
  cancelled_chargeable: "Cancelled (Chargeable)",
  late_report_penalty: "Late Report Penalty",
  regular_cancel_penalty: "Regular Cancel Penalty",
  regular_noshow_penalty: "Regular No-Show Penalty",
  severe_cancel_penalty: "Severe Cancel Penalty",
  severe_noshow_penalty: "Severe No-Show Penalty",
  night_bonus: "Night Bonus",
  group_lesson_bump: "Group Lesson Pay",
  monthly_hour_bonus: "Monthly Hour Bonus",
  counselor_earnings: "Counselor Earnings",
  mentor_earnings: "Mentor Earnings",
};

// Color chips for line type badges — { bg, color }
const LINE_TYPE_CHIP = {
  base_pay:              { bg: "#f1f5f9", color: "#1e293b" },   // slate
  lesson:                { bg: "#dbeafe", color: "#1e3a8a" },   // dark blue
  demo_lesson:           { bg: "#e0f2fe", color: "#0369a1" },   // pale blue
  cancelled_chargeable:  { bg: "#ccfbf1", color: "#0f766e" },   // teal
  demo_conversion_bonus: { bg: "#dcfce7", color: "#166534" },   // green
  counselor_demo_conversion_bonus: { bg: "#ccfbf1", color: "#0f766e" },   // teal
  night_bonus:           { bg: "#ede9fe", color: "#5b21b6" },   // purple
  group_lesson_bump:     { bg: "#cffafe", color: "#155e75" },   // cyan
  monthly_hour_bonus:    { bg: "#fef9c3", color: "#854d0e" },   // amber
  counselor_earnings:    { bg: "#fce7f3", color: "#9d174d" },   // pink
  mentor_earnings:       { bg: "#ffedd5", color: "#9a3412" },   // orange
};

const EARNINGS_TYPES = new Set([
  "lesson", "demo_lesson", "demo_conversion_bonus", "cancelled_chargeable",
  "night_bonus", "group_lesson_bump", "monthly_hour_bonus", "counselor_earnings", "mentor_earnings",
]);

const DEDUCTION_TYPES = new Set([
  "regular_cancel_penalty", "regular_noshow_penalty",
  "severe_cancel_penalty", "severe_noshow_penalty",
]);

const SECTIONS = [
  { key: "base_pay",              title: "Base Pay" },
  { key: "lesson",                title: "Lessons" },
  { key: "demo_lesson",           title: "Demo Lessons" },
  { key: "cancelled_chargeable",  title: "Cancelled (Chargeable)" },
  { key: "counselor_earnings",    title: "Counselor Earnings" },
  { key: "mentor_earnings",       title: "Mentor Earnings" },
  { key: "night_bonus",           title: "Night Bonuses" },
  { key: "group_lesson_bump",     title: "Group Lesson Pay" },
  { key: "monthly_hour_bonus",    title: "Monthly Hour Bonuses" },
  { key: "demo_conversion_bonus", title: "Demo Conversion Bonuses" },
  { key: "counselor_demo_conversion_bonus", title: "Counselor Demo Conversion Bonuses" },
];

const LineItemsTable = ({ lineItems }) => {
  const sortByDate = (items) => [...items].sort((a, b) => {
    if (!a.appointmentStartTime && !b.appointmentStartTime) return 0;
    if (!a.appointmentStartTime) return -1;
    if (!b.appointmentStartTime) return 1;
    return new Date(a.appointmentStartTime) - new Date(b.appointmentStartTime);
  });

  const deductions = sortByDate(lineItems?.filter((i) => DEDUCTION_TYPES.has(i.lineType)) || []);
  const lateReports = sortByDate(lineItems?.filter((i) => i.lineType === "late_report_penalty") || []);

  const styles = {
    card: {
      background: "white",
      borderRadius: "0.5rem",
      boxShadow: "0 1px 3px 0 rgba(0,0,0,0.1)",
      padding: "1.5rem",
      border: "1px solid #e2e8f0",
      marginBottom: "1.5rem",
    },
    cardTitle: { fontSize: "1.3rem", fontWeight: "600", color: "#001272", margin: "0 0 0.75rem 0" },
    sectionTitle: {
      fontSize: "1rem", fontWeight: "600", color: "#001272",
      margin: "1.25rem 0 0.5rem 0", paddingBottom: "0.25rem",
      borderBottom: "1px solid #e2e8f0",
    },
    item: {
      backgroundColor: "white", border: "1px solid #e2e8f0",
      borderRadius: "0.375rem", padding: "0.75rem",
      marginBottom: "0.5rem", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    },
    itemHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.35rem" },
    itemTitle: { fontWeight: "600", color: "#001272", fontSize: "0.95rem" },
    itemDesc: { fontSize: "0.85rem", color: "#4b5563", marginTop: "0.1rem" },
    amount: (negative) => ({
      fontWeight: "600", fontSize: "1.05rem",
      color: negative ? "#dc2626" : "#0369a1",
    }),
    details: { display: "flex", flexWrap: "wrap", gap: "1rem", paddingTop: "0.5rem", borderTop: "1px solid #f1f5f9", marginTop: "0.35rem" },
    detailLabel: { fontSize: "0.75rem", color: "#64748b", fontWeight: "500", marginBottom: "0.1rem" },
    detailValue: { fontSize: "0.85rem", color: "#4b5563", fontWeight: "500" },
  };

  const isLateReport = item => item.lineType === "late_report_penalty";

  const renderItem = (item, i) => {
    const label = LINE_TYPE_LABEL[item.lineType] || item.lineType;
    const chip = LINE_TYPE_CHIP[item.lineType];
    const isWaived = item.penaltyWaived === true;
    const isNegative = !isWaived && item.amount < 0;
    const showHoursRate = item.hours != null && item.hourlyRate != null
      && item.lineType !== "demo_lesson"
      && item.lineType !== "demo_conversion_bonus"
      && item.lineType !== "counselor_demo_conversion_bonus";
    const title = item.appointmentTopic || label;
    const tz = item.branchTimezone || null;
    const abbr = tzAbbr(tz);
    const startTime = fmtDateTime(item.appointmentStartTime, tz);

    return (
      <div key={item.id ?? i} style={styles.item}>
        <div style={styles.itemHeader}>
          <div>
            <div style={styles.itemTitle}>{title}</div>
            <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: "0.1rem", display: "flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap" }}>
              {chip ? (
                <span style={{ display: "inline-block", fontSize: "0.7rem", fontWeight: "600", padding: "0.1rem 0.45rem", borderRadius: "9999px", background: chip.bg, color: chip.color }}>{label}</span>
              ) : (
                <span>{label}</span>
              )}
              {startTime && <span>{startTime}{abbr && <span style={{ color: "#9ca3af" }}> {abbr}</span>}</span>}
              {item.tcAppointmentId && (
                <span style={{ color: "#9ca3af" }}>TC #{item.tcAppointmentId}</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            {isWaived ? (
              <>
                <span style={{ fontWeight: "600", fontSize: "1.05rem", color: "#64748b" }}>$0.00</span>
                <span style={{ fontSize: "0.65rem", fontWeight: "700", padding: "0.1rem 0.4rem", borderRadius: "9999px", background: "#fef9c3", color: "#854d0e" }}>Waived</span>
              </>
            ) : (
              <div style={styles.amount(isNegative)}>{formatCurrency(item.amount)}</div>
            )}
          </div>
        </div>

        {isLateReport(item) && (
          <div style={{ fontSize: "0.78rem", color: "#b45309", marginTop: "0.35rem", paddingTop: "0.35rem", borderTop: "1px solid #fde68a" }}>
            {item.appointmentStartTime && (
              <div style={{ color: "#94a3b8" }}>
                Lesson started: <strong>{fmtDateTime(item.appointmentStartTime, tz)}{abbr && <span style={{ fontWeight: "normal" }}> {abbr}</span>}</strong>
              </div>
            )}
            {item.appointmentFinishTime && (
              <div style={{ color: "#94a3b8" }}>
                Lesson ended: <strong>{fmtDateTime(item.appointmentFinishTime, tz)}{abbr && <span style={{ fontWeight: "normal" }}> {abbr}</span>}</strong>
              </div>
            )}
            {item.reportSubmittedAt
              ? (() => {
                  const lateMs = new Date(item.reportSubmittedAt) - new Date(item.appointmentFinishTime);
                  const totalMin = Math.floor(lateMs / 60000);
                  const d = Math.floor(totalMin / 1440);
                  const h = Math.floor((totalMin % 1440) / 60);
                  const m = totalMin % 60;
                  const parts = [];
                  if (d > 0) parts.push(`${d}d`);
                  if (h > 0) parts.push(`${h}h`);
                  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
                  return (
                    <div>
                      Report submitted: <strong>{fmtDateTime(item.reportSubmittedAt, tz)}{abbr && <span style={{ fontWeight: "normal" }}> {abbr}</span>}</strong> (after {parts.join(" ")})
                    </div>
                  );
                })()
              : <div>Report submitted late (&gt;24h after lesson end)</div>}
          </div>
        )}

        {(showHoursRate || item.description) && (
          <div style={styles.details}>
            {showHoursRate && (
              <div>
                <div style={styles.detailLabel}>Rate</div>
                <div style={styles.detailValue}>
                  {item.hours.toFixed(2)}h × {formatCurrency(item.hourlyRate)}/hr
                </div>
              </div>
            )}
            {item.description && !isLateReport(item) && (
              <div>
                <div style={styles.detailLabel}>Note</div>
                <div style={styles.detailValue}>{item.description}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const hasItems = lineItems && lineItems.length > 0;

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Line Items</h2>

      {!hasItems ? (
        <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "0.375rem", padding: "1.5rem", textAlign: "center", color: "#64748b" }}>
          No line items available for this payroll.
        </div>
      ) : (
        <>
          {SECTIONS.map(({ key, title }) => {
            const items = sortByDate(lineItems.filter((i) => i.lineType === key));
            if (!items.length) return null;
            return (
              <React.Fragment key={key}>
                <h3 style={styles.sectionTitle}>{title} ({items.length})</h3>
                {items.map(renderItem)}
              </React.Fragment>
            );
          })}
          {deductions.length > 0 && (
            <>
              <h3 style={{ ...styles.sectionTitle, color: "#dc2626" }}>Deductions ({deductions.length})</h3>
              {deductions.map(renderItem)}
            </>
          )}
          {lateReports.length > 0 && (
            <>
              <h3 style={{ ...styles.sectionTitle, color: "#92400e" }}>
                Late Reports ({lateReports.length}{lateReports.filter(i => i.penaltyWaived).length > 0 && `, ${lateReports.filter(i => i.penaltyWaived).length} waived`})
              </h3>
              {lateReports.map(renderItem)}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LineItemsTable;
